// apps/web/src/App.tsx
import * as React from 'react';
import { BookOpen, Dumbbell, CalendarPlus, ListChecks, CheckCircle, ShoppingBag, MoreHorizontal, X, ClipboardList, Users, Coffee, User } from 'lucide-react';
import { useStartedTraining } from './context/StartedTrainingContext';
import StartedTrainingCard from './components/StartedTrainingCard';
import PartnerInvitePrompt from './components/PartnerInvitePrompt';
import PartnerStartedCard from './components/PartnerStartedCard';
import { AuthLock } from './components/AuthLock';
import { showToast } from './components/ui/Toast';
import AuthPanel from './components/AuthPanel';
import { CookieBanner } from './components/CookieBanner';
import { useLocale } from './context/LocaleContext';
import { useSwipeDotsValue } from './context/SwipeDotsContext';
import { usePageSlide } from './hooks/usePageSlide';
import { useAuth } from './auth/AuthProvider';
import { useProfile } from './context/ProfileContext';
import { isNativeApp, openExternalUrl } from './lib/appPlatform';
import { syncServerClock } from './lib/clockSync';
import { hasWorkoutManagerAccess } from './lib/planAccess';
import { authFetchJSON } from './lib/authFetch';
import { API_BASE } from './lib/apiBase';

import PrCelebrationHost from './components/PrCelebrationHost';
// lazy load existing components
const Header = React.lazy(() => import('./components/Header'));
const TrainingMethodsCard = React.lazy(() => import('./components/TrainingMethodsCard'));
const ExercisesCard = React.lazy(() => import('./components/ExercisesCard'));
const CreateTrainingCard = React.lazy(() => import('./components/CreateTrainingCard'));
const PlannedTrainingCard = React.lazy(() => import('./components/PlannedTrainingCard'));
const CoachPlansCard = React.lazy(() => import('./components/CoachPlansCard'));
const CompletedTrainingCard = React.lazy(() => import('./components/CompletedTrainingCard'));
const WorkoutPlanShop = React.lazy(() => import('./components/WorkoutPlanShop'));
const WorkoutManagerCard = React.lazy(() => import('./components/WorkoutManagerCard'));
const ProfileCard = React.lazy(() => import('./components/ProfileCard'));
const TrainingStatsPanel = React.lazy(() => import('./components/TrainingStatsPanel'));
const FriendsCard = React.lazy(() => import('./components/FriendsCard'));
const InfoCard = React.lazy(() => import('./components/InfoCard'));

type TabKey = 'planned' | 'coach' | 'methods' | 'exercises' | 'planner'| 'completed' | 'planshop' | 'workoutManager' | 'profile' | 'stats' | 'friends' | 'info';

const TABS: { key: TabKey; titleKey: string; icon: React.ReactNode; render: () => JSX.Element }[] = [
  { key: 'planner',   titleKey: 'Create Training',  icon: <CalendarPlus size={16} />, render: () => <CreateTrainingCard /> },
  { key: 'planned',   titleKey: 'Planned Training', icon: <ListChecks size={16} />, render: () => <PlannedTrainingCard /> },
  { key: 'coach',     titleKey: 'Booking', icon: <Users size={16} />, render: () => <CoachPlansCard /> },
  { key: 'completed', titleKey: 'Completed',        icon: <CheckCircle size={16} />, render: () => <CompletedTrainingCard /> },
  { key: 'planshop',  titleKey: 'Workout Plan Shop',    icon: <ShoppingBag size={16} />, render: () => <WorkoutPlanShop /> },
  { key: 'methods',   titleKey: 'Training Methods', icon: <BookOpen size={16} />,   render: () => <TrainingMethodsCard /> },
  { key: 'exercises', titleKey: 'Exercises',        icon: <Dumbbell size={16} />,   render: () => <ExercisesCard /> },
  { key: 'workoutManager', titleKey: 'Workout Manager', icon: <ClipboardList size={16} />, render: () => <WorkoutManagerCard /> },
  { key: 'profile',   titleKey: 'Profile',          icon: <User size={16} />,       render: () => <ProfileCard /> },
  { key: 'stats',     titleKey: 'Training Stats',   icon: <CheckCircle size={16} />, render: () => <TrainingStatsPanel /> },
  { key: 'friends',   titleKey: 'Friends',          icon: <Users size={16} />,       render: () => <FriendsCard /> },
  { key: 'info',      titleKey: 'Info',             icon: <BookOpen size={16} />,    render: () => <InfoCard /> },
];

function OverlayStartedTraining() {
  const { started, partner } = useStartedTraining();
  if (!started) return null;
  if (partner) return <PartnerStartedCard training={started} partner={partner} />;
  return <StartedTrainingCard training={started} />;
}

export default function App() {
  const { t } = useLocale();
  const { session } = useAuth();
  const { profile } = useProfile();
  const [active, setActive] = React.useState<TabKey>('planned');
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [isMobileViewport, setIsMobileViewport] = React.useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });

  const [authOpen, setAuthOpen] = React.useState(false);
  // Mirrors AuthPanel's own mode so the modal header does not keep saying
  // "Sign in" after the user has switched to signing up.
  const [authMode, setAuthMode] = React.useState<'signin' | 'signup'>('signin');
  const [coffeeLoading, setCoffeeLoading] = React.useState(false);

  // Page-dots indicator for the active screen's swipeable sub-tabs (published by
  // that screen via useSwipeDots); rendered near the bottom, above the mobile nav.
  const swipeDots = useSwipeDotsValue();

  const handleCoffeeCheckout = React.useCallback(async () => {
    if (coffeeLoading) return;
    setCoffeeLoading(true);
    const res = await authFetchJSON<{ url: string }>(
      `${API_BASE}/api/billing/coffee-checkout`,
      { method: 'POST' }
    );
    setCoffeeLoading(false);
    if (!res.ok || !res.data?.url) {
      showToast(res.status === 401 ? t('Please sign in first.') : res.errorText || 'Could not start checkout.');
      return;
    }
    await openExternalUrl(res.data.url);
  }, [coffeeLoading, t]);
  const stableAppHeightRef = React.useRef(0);
  const bottomNavRef = React.useRef<HTMLElement>(null);
  const nativeApp = React.useMemo(() => isNativeApp(), []);

  const renderIcon = React.useCallback((icon: React.ReactNode, size = 20) => {
    if (React.isValidElement(icon)) {
      return React.cloneElement(icon, { size });
    }
    return icon;
  }, []);

  const workoutManagerUnlocked = hasWorkoutManagerAccess(
    profile?.plan,
    profile?.workoutManagerUnlocked
  );

  const tabs = React.useMemo(() => {
    // The single-email "Image to Text" tab used to be filtered out here. Reading a
    // workout out of an image now lives in the AI Planner's Screenshot mode, where
    // it maps to the catalog instead of inventing exercise rows.
    const visibleTabs = workoutManagerUnlocked ? TABS : TABS.filter(tab => tab.key !== 'workoutManager');
    return visibleTabs.map(tab => ({
      ...tab,
      title: t(tab.titleKey),
    }));
  }, [t, workoutManagerUnlocked]);
  const isPwa = React.useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      (window.matchMedia?.('(display-mode: standalone)').matches ?? false) ||
      (window.navigator as any)?.standalone === true
    );
  }, []);
  const useFixedMobileViewport = isPwa || nativeApp || isMobileViewport;
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    root.classList.toggle('pwa-standalone', isPwa);
    root.classList.toggle('native-app', nativeApp);
    if (!useFixedMobileViewport) {
      root.classList.remove('mobile-app-shell-fixed');
      root.classList.remove('pwa-standalone');
      root.style.removeProperty('--app-height');
      stableAppHeightRef.current = 0;
      return;
    }
    root.classList.add('mobile-app-shell-fixed');
    let rafId = 0;
    let timeouts: number[] = [];
    const clearTimers = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      timeouts.forEach(clearTimeout);
      timeouts = [];
    };
    const updateHeight = () => {
      const vv = window.visualViewport;
      const innerHeight = Math.round(window.innerHeight);
      const vvHeight = Math.round(vv?.height ?? innerHeight);
      const vvOffsetTop = Math.round(vv?.offsetTop ?? 0);
      const viewportHeight = vvHeight + vvOffsetTop;
      const docHeight = Math.round(document.documentElement.clientHeight);
      const screenHeight = Math.round(window.screen?.height ?? 0);
      const measuredMax = Math.max(innerHeight, docHeight, viewportHeight);
      const stableCandidate = isPwa || nativeApp
        ? (screenHeight ? Math.max(measuredMax, screenHeight) : measuredMax)
        : measuredMax;
      const baseHeight = stableAppHeightRef.current || stableCandidate;
      const heightDelta = Math.abs(baseHeight - viewportHeight);
      if (!stableAppHeightRef.current || heightDelta < 80) {
        stableAppHeightRef.current = stableCandidate;
      }
      const lockedHeight = stableAppHeightRef.current || stableCandidate;
      root.style.setProperty('--app-height', `${Math.round(lockedHeight)}px`);
    };
    const scheduleHeightUpdate = () => {
      clearTimers();
      updateHeight();
      rafId = requestAnimationFrame(updateHeight);
      const settleDelays = [120, 360, 900, 1500, 2200];
      timeouts = settleDelays.map((delay) => window.setTimeout(updateHeight, delay));
    };
    const resetHeightLock = () => {
      stableAppHeightRef.current = 0;
      scheduleHeightUpdate();
    };
    scheduleHeightUpdate();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('orientationchange', resetHeightLock);
    window.addEventListener('pageshow', resetHeightLock);
    window.visualViewport?.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('orientationchange', resetHeightLock);
      window.removeEventListener('pageshow', resetHeightLock);
      window.visualViewport?.removeEventListener('resize', updateHeight);
      clearTimers();
    };
  }, [isPwa, nativeApp, useFixedMobileViewport]);

  // On iOS, position:fixed elements follow vv.offsetTop when the keyboard opens and
  // the page scrolls to reveal the focused input — the header flies off the top and
  // the nav floats above the keyboard. Fix: counteract vv.offsetTop on the header,
  // and hide the nav entirely. Use a stable baseline height (captured at mount) for
  // keyboard detection, because on modern iOS window.innerHeight shrinks with vv.height.
  React.useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const stableHeight = { current: vv.height };
    const resetStable = () => { stableHeight.current = vv.height; };

    let rafId = 0;
    let looping = false;

    // Pin the fixed header to the top of the visual viewport and hide the nav.
    // Returns whether the keyboard is currently open.
    const apply = () => {
      const nav = bottomNavRef.current;
      const header = document.getElementById('app-header-outer') as HTMLElement | null;
      const keyboardOpen = stableHeight.current - vv.height > 150;
      if (header) {
        const offset = keyboardOpen && vv.offsetTop > 0 ? vv.offsetTop : 0;
        header.style.transform = offset > 0 ? `translateY(${offset}px)` : '';
      }
      if (nav) {
        nav.style.display = keyboardOpen ? 'none' : '';
      }
      return keyboardOpen;
    };

    // iOS fires visualViewport scroll/resize a frame late, which makes the
    // header visibly lag during scroll. While the keyboard is open, re-pin every
    // animation frame so the header tracks the viewport without jitter.
    const loop = () => {
      if (apply()) {
        rafId = requestAnimationFrame(loop);
      } else {
        looping = false;
      }
    };
    const update = () => {
      const open = apply();
      if (open && !looping) {
        looping = true;
        rafId = requestAnimationFrame(loop);
      }
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('orientationchange', resetStable);
    return () => {
      cancelAnimationFrame(rafId);
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('orientationchange', resetStable);
    };
  }, []);

  // Keep the server/client clock offset fresh so coach workout timers stay in
  // sync across devices. Re-sync when the app returns to the foreground.
  React.useEffect(() => {
    void syncServerClock();
    const resync = () => { if (document.visibilityState === 'visible') void syncServerClock(); };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(max-width: 767px)');
    const onChange = () => setIsMobileViewport(media.matches);
    onChange();
    if (media.addEventListener) {
      media.addEventListener('change', onChange);
      return () => media.removeEventListener('change', onChange);
    }
    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);
  React.useEffect(() => {
    if (typeof document === 'undefined' || !useFixedMobileViewport) return;

    const preventGestureZoom = (event: Event) => {
      event.preventDefault();
    };
    const preventPinchZoom = (event: Event) => {
      const touchEvent = event as TouchEvent & { scale?: number };
      const isPinch =
        (typeof touchEvent.scale === 'number' && touchEvent.scale !== 1) ||
        ((touchEvent.touches?.length ?? 0) > 1);
      if (isPinch) {
        event.preventDefault();
      }
    };

    document.addEventListener('gesturestart', preventGestureZoom, { passive: false });
    document.addEventListener('gesturechange', preventGestureZoom, { passive: false });
    document.addEventListener('gestureend', preventGestureZoom, { passive: false });
    document.addEventListener('touchmove', preventPinchZoom, { passive: false });

    return () => {
      document.removeEventListener('gesturestart', preventGestureZoom);
      document.removeEventListener('gesturechange', preventGestureZoom);
      document.removeEventListener('gestureend', preventGestureZoom);
      document.removeEventListener('touchmove', preventPinchZoom);
    };
  }, [useFixedMobileViewport]);
  const isPwaLikeMobile = isPwa || isMobileViewport;
  const mobileTabs = React.useMemo(
    () => {
      const excluded = new Set<TabKey>(['methods', 'exercises', 'workoutManager', 'profile', 'stats', 'friends', 'info']);
      if (isPwaLikeMobile) {
        excluded.add('completed');
        excluded.add('coach');
      }
      return tabs.filter(tab => !excluded.has(tab.key));
    },
    [tabs, isPwaLikeMobile]
  );
  const moreItems = React.useMemo(
    () =>
      tabs
        .filter(tab =>
          tab.key === 'methods' ||
          tab.key === 'exercises' ||
          tab.key === 'workoutManager' ||
          (isPwaLikeMobile && tab.key === 'coach') ||
          (isPwaLikeMobile && tab.key === 'completed')
        )
        .map(tab => ({
          key: tab.key,
          label:
            tab.key === 'methods'
              ? t('Methods')
              : tab.key === 'workoutManager' && isPwaLikeMobile
                ? t('Coach')
                : tab.title,
          icon: tab.icon,
        })),
    [tabs, t, isPwaLikeMobile]
  );
  const showMore = moreItems.length > 0;
  const moreHasActive = React.useMemo(() => moreItems.some(item => item.key === active), [moreItems, active]);

  React.useEffect(() => {
    if (!tabs.some(tab => tab.key === active)) {
      setActive(tabs[0]?.key ?? 'planned');
    }
  }, [tabs, active]);
  React.useEffect(() => {
    const onSelectTab = (event: Event) => {
      const detail = (event as CustomEvent<TabKey>).detail;
      if (detail && tabs.some(tab => tab.key === detail)) {
        setActive(detail);
        setMoreOpen(false);
      }
    };
    window.addEventListener('app:select-tab', onSelectTab as EventListener);
    return () => window.removeEventListener('app:select-tab', onSelectTab as EventListener);
  }, [tabs]);

  // optional: remember selection across reloads
  React.useEffect(() => {
    const saved = localStorage.getItem('activeTab') as TabKey | null;
    if (saved && tabs.some(t => t.key === saved)) setActive(saved);
  }, [tabs]);
  React.useEffect(() => {
    localStorage.setItem('activeTab', active);
  }, [active]);

  // Handle Stripe redirect params for workout plan purchase
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchase = params.get('planPurchase');
    if (purchase) {
      if (purchase === 'success') {
        showToast(t('Workout plan purchased! You can apply it now.', 'Workout plan purchased! You can apply it now.'), 'success');
        setActive('planshop');
        try {
          const userId = session?.user?.id ?? null;
          const key = userId ? `planPurchaseThanks:${userId}` : 'planPurchaseThanks';
          localStorage.setItem(key, '1');
          window.dispatchEvent(new CustomEvent('notifications:planPurchase', { detail: key }));
        } catch {
          // ignore
        }
      } else if (purchase === 'cancel') {
        showToast(t('Purchase cancelled', 'Purchase cancelled'));
      }
      params.delete('planPurchase');
      params.delete('plan');
      const newQuery = params.toString();
      const newUrl = `${window.location.origin}${window.location.pathname}${newQuery ? `?${newQuery}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [t, session?.user?.id]);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const studioParam = params.get('studioFriendId') || params.get('friendId');
    if (!studioParam) return;
    const trimmed = studioParam.trim();
    if (!trimmed) return;

    const storageKey = `studioFriendId:${session?.user?.id ?? 'local'}`;
    try {
      localStorage.setItem(storageKey, trimmed);
    } catch {
      // ignore
    }
    try {
      window.dispatchEvent(new CustomEvent('studioFriendId:updated', { detail: trimmed }));
    } catch {
      // ignore
    }
    setActive('coach');

    if (session?.user?.id) {
      params.delete('studioFriendId');
      params.delete('friendId');
      params.delete('tab');
      const newQuery = params.toString();
      const newUrl = `${window.location.origin}${window.location.pathname}${newQuery ? `?${newQuery}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [session?.user?.id]);

// Listen for global events to open/close the auth modal
  React.useEffect(() => {
    const onOpen = () => setAuthOpen(true);
    const onClose = () => setAuthOpen(false);
    const onChanged = () => setAuthOpen(false);
    window.addEventListener('auth:open' as any, onOpen);
    window.addEventListener('auth:close' as any, onClose);
    window.addEventListener('auth:changed' as any, onChanged);
	
	document.title = 'Probell';

    // Close on ESC
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAuthOpen(false);
    };
    window.addEventListener('keydown', onKey);

    return () => {
      window.removeEventListener('auth:open' as any, onOpen);
      window.removeEventListener('auth:close' as any, onClose);
      window.removeEventListener('auth:changed' as any, onChanged);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const ActiveView = React.useMemo(() => tabs.find(t => t.key === active) ?? tabs[0], [active, tabs]);
  // Slide the centre stage when switching main screens (bottom nav / left rail),
  // matching the sub-tab swipe animation. Direction follows the nav order.
  const tabOrder = React.useMemo(() => tabs.map(t => t.key), [tabs]);
  const panelRef = usePageSlide<TabKey, HTMLElement>(tabOrder, active);
  const lockLabelByTab: Record<string, string> = React.useMemo(() => ({
    planned:  t('Login to view and manage planned trainings'),
    coach: t('Login to view coach plans', 'Login to view coach plans'),
    today:    t("Login to view today's training"),
    completed: t('Login to view your completed trainings'),
    planshop: t('Login to buy or use workout plans'),
    methods:  t('Login to browse methods'),
    exercises: t('Login to browse exercises'),
    planner:  t('Login to create a training'),
    workoutManager: t('Login to use Workout Manager'),
  }), [t]);
  const lockLabel = lockLabelByTab[active] ?? t('Login to use this feature');
  
  return (
    <div className="app-shell relative min-h-dvh overflow-hidden text-slate-900 dark:text-slate-100">
      <div className="pointer-events-none absolute -top-44 left-1/2 h-80 w-[520px] -translate-x-1/2 rounded-full bg-brand-500/35 blur-3xl dark:bg-blue-500/20" />
      <div className="pointer-events-none absolute top-24 -left-28 h-64 w-64 rounded-full bg-cyan-300/30 blur-3xl dark:bg-cyan-500/15" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-80 w-80 rounded-full bg-indigo-400/30 blur-3xl dark:bg-indigo-500/20" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/55 via-white/12 to-transparent dark:from-slate-900/45 dark:via-slate-900/18" />
      <div className="pointer-events-none absolute inset-0 bg-white/5 dark:bg-slate-900/10 backdrop-blur-sm" />
      <div className="relative z-10" style={{ paddingTop: 'var(--app-header-height, 0px)' }}>
        <React.Suspense fallback={<div className="p-4 text-sm opacity-70">Loading header...</div>}>
          <Header activeTab={active} />
        </React.Suspense>
        <PrCelebrationHost />

        <main className="app-main mx-auto max-w-6xl px-2 md:px-4 pt-3 pb-[calc(var(--bottom-nav-bar-h,72px)+6px+max(var(--safe-bottom,0px),2px))] md:pt-10 md:pb-10">
          <div className="app-content grid grid-cols-1 md:grid-cols-[240px_minmax(0,1fr)] gap-6">
            {/* Left rail */}
            <nav
              aria-label="Sections"
              className="hidden md:block md:sticky md:top-[calc(var(--app-header-height,0px)+1.5rem)] h-max rounded-2xl glass p-2"
            >
              <ul className="space-y-1">
                {tabs.filter(tab => !['profile', 'stats', 'friends', 'info'].includes(tab.key)).map(tab => {
                  const isActive = tab.key === active;
                  return (
                    <li key={tab.key}>
                      <button
                        className={[
                          'w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm transition',
                          isActive
                            ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 shadow'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-900/40'
                        ].join(' ')}
                        onClick={() => setActive(tab.key)}
                      >
                        {tab.icon}
                        <span className="truncate">{tab.title}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Center stage */}
            <OverlayStartedTraining />
            <PartnerInvitePrompt />
            <section
              ref={panelRef}
              className="app-panel rounded-2xl glass p-3 sm:p-4 md:p-6"
              role="region"
              aria-live="polite"
            >
              <React.Suspense
                fallback={
                  <div className="animate-pulse">
                    <div className="h-6 w-40 rounded bg-slate-200 dark:bg-slate-800 mb-4" />
                    <div className="h-24 rounded bg-slate-200 dark:bg-slate-800" />
                  </div>
                }
              >
                <AuthLock label={lockLabel}>
                  {ActiveView.render()}
                </AuthLock>
              </React.Suspense>
            </section>
          </div>
        </main>

        {showMore && moreOpen && (
          <div className="fixed inset-0 z-[920] md:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-black/20"
              onClick={() => setMoreOpen(false)}
              aria-label={t('Close')}
            />
            <div className="absolute inset-x-0 bottom-[calc(max(var(--safe-bottom,0px),2px)+4.5rem)] mx-auto max-w-6xl px-3 z-10">
              <div className="rounded-3xl glass p-2">
                <div className="grid grid-cols-4 gap-2">
                  {moreItems.map(item => {
                    const isItemActive = item.key === active;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => {
                          setActive(item.key);
                          setMoreOpen(false);
                        }}
                        className={[
                          'flex flex-col items-center gap-2 rounded-2xl px-1 pt-3 pb-2 transition',
                          isItemActive
                            ? 'border border-brand-500/60 bg-brand-50/80 text-brand-800 dark:border-brand-400/70 dark:bg-brand-500/15 dark:text-white'
                            : 'text-slate-800 dark:text-slate-100 hover:bg-white/60 dark:hover:bg-slate-900/60'
                        ].join(' ')}
                      >
                        <span className="grid h-14 w-14 place-items-center rounded-2xl transition glass-icon text-slate-700 dark:text-slate-100">
                          {renderIcon(item.icon, 22)}
                        </span>
                        <span className="text-[12px] font-semibold leading-tight text-center w-full min-h-[2rem] line-clamp-2">
                          {item.label}
                        </span>
                      </button>
                    );
                  })}

                  {/* Buy me a Coffee — web only; Apple requires IAP for in-app tips */}
                  {!nativeApp && (
                  <button
                    type="button"
                    disabled={coffeeLoading}
                    onClick={() => { setMoreOpen(false); handleCoffeeCheckout(); }}
                    className="flex flex-col items-center gap-2 rounded-2xl px-1 pt-3 pb-2 transition text-slate-800 dark:text-slate-100 hover:bg-white/60 dark:hover:bg-slate-900/60 disabled:opacity-50"
                  >
                    <span className="grid h-14 w-14 place-items-center rounded-2xl transition glass-icon text-amber-600 dark:text-amber-400">
                      <Coffee size={22} />
                    </span>
                    <span className="text-[12px] font-semibold leading-tight text-center w-full min-h-[2rem] line-clamp-2">
                      {coffeeLoading ? t('Loading…') : t('Buy me a Coffee')}
                    </span>
                  </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Swipe page-dots — sit in the gap above the bottom nav; reflect the
            active screen's swipeable sub-tabs and signal that you can swipe. */}
        {swipeDots && swipeDots.count > 1 && (
          <div
            className="pointer-events-none fixed inset-x-0 z-[901] md:hidden flex justify-center"
            style={{ bottom: 'calc(max(var(--safe-bottom,0px),2px) + var(--bottom-nav-bar-h,72px) + 8px)' }}
          >
            <div className="pointer-events-auto flex items-center gap-2">
              {Array.from({ length: swipeDots.count }).map((_, i) => {
                const on = i === swipeDots.index;
                return (
                  <button
                    key={i}
                    type="button"
                    aria-label={`${t('Page')} ${i + 1}`}
                    aria-current={on ? 'page' : undefined}
                    onClick={() => swipeDots.onSelect(i)}
                    className={[
                      'rounded-full transition-all',
                      on ? 'w-2 h-2 bg-slate-700 dark:bg-slate-200' : 'w-1.5 h-1.5 bg-slate-400/60 dark:bg-slate-500/60',
                    ].join(' ')}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Mobile bottom nav */}
        <nav ref={bottomNavRef} className="fixed inset-x-0 bottom-0 z-[900] md:hidden pointer-events-none">
          <div className="mx-auto max-w-6xl px-2 pb-[max(var(--safe-bottom,0px),2px)] pt-0">
            <div className="pointer-events-auto relative overflow-hidden rounded-3xl glass">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/35 via-white/10 to-transparent" />
              <div
                className="relative flex items-stretch gap-1 overflow-hidden px-1.5"
                style={{ minHeight: 'var(--bottom-nav-bar-h, 72px)' }}
              >
                {mobileTabs.map(tab => {
                  const isActive = tab.key === active;
                  const icon = renderIcon(tab.icon, 22);
                  const mobileLabel =
                    tab.key === 'planner'
                      ? t('Create')
                      : tab.key === 'planned'
                        ? t('Planned')
                        : tab.key === 'coach'
                          ? t('Booking')
                        : tab.key === 'planshop'
                          ? t('Plan Shop')
                          : tab.key === 'methods'
                            ? t('Methods')
                            : tab.key === 'workoutManager' && isPwaLikeMobile
                              ? t('Coach')
                              : tab.title;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => {
                        setActive(tab.key);
                        setMoreOpen(false);
                      }}
                      className={[
                        'flex-1 min-w-0 h-full flex flex-col items-center justify-center gap-0.5 px-1 py-1 rounded-2xl transition',
                        isActive
                          ? 'text-brand-700 dark:text-brand-200'
                          : 'text-slate-500 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                      ].join(' ')}
                      aria-label={tab.title}
                    >
                    <span
                      className={[
                        'grid h-9 w-9 place-items-center rounded-xl transition glass-icon',
                        isActive
                          ? 'border-2 border-brand-500 bg-brand-50/90 text-brand-700 shadow-[0_12px_24px_-18px_rgba(37,99,235,0.45)] dark:border-brand-400 dark:bg-brand-500/15 dark:text-white'
                          : 'text-slate-700 dark:text-gray-200'
                      ].join(' ')}
                    >
                        {icon}
                      </span>
                      <span className="text-[13px] font-semibold leading-tight truncate max-w-full text-center w-full">
                        {mobileLabel}
                      </span>
                    </button>
                  );
                })}
                {showMore && (
                  <button
                    type="button"
                    onClick={() => setMoreOpen(open => !open)}
                    className={[
                      'flex-1 min-w-0 h-full flex flex-col items-center justify-center gap-0.5 px-1 py-1 rounded-2xl transition',
                      moreOpen || moreHasActive
                        ? 'text-brand-700 dark:text-brand-200'
                        : 'text-slate-500 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    ].join(' ')}
                    aria-label={t('More')}
                    aria-expanded={moreOpen}
                    aria-haspopup="menu"
                  >
                    <span
                      className={[
                        'grid h-9 w-9 place-items-center rounded-xl transition glass-icon',
                        moreOpen || moreHasActive
                          ? 'border-2 border-brand-500 bg-brand-50/90 text-brand-700 shadow-[0_12px_24px_-18px_rgba(37,99,235,0.45)] dark:border-brand-400 dark:bg-brand-500/15 dark:text-white'
                          : 'text-slate-700 dark:text-gray-200'
                      ].join(' ')}
                    >
                      {renderIcon(<MoreHorizontal />, 22)}
                    </span>
                    <span className="text-[13px] font-semibold leading-tight truncate max-w-full text-center w-full">
                      {t('More')}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[850] md:hidden"
          style={{ height: 'var(--safe-bottom)' }}
          aria-hidden="true"
        />
        <CookieBanner />

        {/* AUTH MODAL */}
        {authOpen && (
          <div
            className="fixed inset-x-0 top-0 z-[1000] flex items-center justify-center overflow-y-auto p-4 pb-[calc(var(--app-header-bar-height,86px)+var(--safe-bottom,0px)+1rem)]"
            style={{ height: 'var(--app-height, 100dvh)' }}
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setAuthOpen(false)}
              aria-hidden="true"
            />
            {/* Dialog */}
            <div
              className="relative w-full max-w-md rounded-2xl bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 shadow-xl flex flex-col"
              style={{ maxHeight: 'calc(var(--app-height, 100dvh) - 2rem)' }}
            >
              <div className="px-4 py-1 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                <div className="font-semibold">{authMode === 'signup' ? t('Sign up') : t('Sign in')}</div>
                  <button className="btn-ghost text-sm inline-flex items-center gap-2" onClick={() => setAuthOpen(false)}>
                  <X size={14} /> {t('Close')}
                </button>
              </div>
              <div className="auth-modal-body p-4 flex-1 min-h-0 overflow-y-auto">
                <AuthPanel onModeChange={setAuthMode} variant="modal" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
